import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Detects exit intent and opens once per page load.
 * - Desktop: cursor leaves the window near the top edge (tabs / close button).
 * - Back button (desktop + mobile): the first "back" press opens the popup
 *   instead of leaving; the next one leaves normally.
 * - Mobile: returning to the tab after switching apps, or a fast upward
 *   scroll after reading part of the page.
 */
export function useExitIntent(armDelayMs = 3000) {
  const [open, setOpen] = useState(false)
  const shownRef = useRef(false)

  const trigger = useCallback(() => {
    if (shownRef.current) return
    shownRef.current = true
    setOpen(true)
  }, [])

  useEffect(() => {
    let armed = false
    let backGuardPushed = false

    const armTimer = window.setTimeout(() => {
      armed = true
      // Back-button guard: add one history entry on the same URL.
      try {
        window.history.pushState(
          { ...(window.history.state ?? {}), exitIntentGuard: true },
          '',
          window.location.href,
        )
        backGuardPushed = true
      } catch {
        backGuardPushed = false
      }
    }, armDelayMs)

    // Desktop: leaving through the top of the viewport.
    const handleMouseOut = (event: MouseEvent) => {
      if (!armed || event.relatedTarget) return
      if (event.clientY < 60) trigger()
    }

    // Back button.
    const handlePopState = () => {
      if (!backGuardPushed || shownRef.current) return
      backGuardPushed = false
      trigger()
    }

    // Mobile: user switched apps/tabs and came back.
    let wasHidden = false
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        wasHidden = true
      } else if (wasHidden && armed) {
        trigger()
      }
    }

    // Mobile: fast upward scroll after reading part of the page.
    let lastY = window.scrollY
    let lastTime = performance.now()
    let maxDepth = 0
    const handleScroll = () => {
      const y = window.scrollY
      const now = performance.now()
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight
      if (docHeight > 0) maxDepth = Math.max(maxDepth, y / docHeight)
      const velocity = (lastY - y) / Math.max(now - lastTime, 1)
      if (armed && maxDepth > 0.3 && velocity > 1.5 && lastY - y > 100) {
        trigger()
      }
      lastY = y
      lastTime = now
    }

    const isTouch = window.matchMedia('(pointer: coarse)').matches

    document.addEventListener('mouseout', handleMouseOut)
    window.addEventListener('popstate', handlePopState)
    document.addEventListener('visibilitychange', handleVisibility)
    if (isTouch) {
      window.addEventListener('scroll', handleScroll, { passive: true })
    }

    return () => {
      window.clearTimeout(armTimer)
      document.removeEventListener('mouseout', handleMouseOut)
      window.removeEventListener('popstate', handlePopState)
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [armDelayMs, trigger])

  const close = useCallback(() => setOpen(false), [])

  return { open, close }
}
